import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import {
  ConferenceStreamsListService,
  LocalConferenceStreamsListService
} from "@youm-front/conference-stream/data-access";
import {
  RoomLocalStreamsSelectors,
  RoomRemoteStreamsSelectors
} from "@youm-front/room/data-access";
import { firstValueFrom } from "rxjs";
import {
  AnyConferenceStream,
  screenShareDiagnosticsSnapshots
} from "youm-lib/conference";

@Injectable()
export class TelemetryDataService {
  private readonly roomLocalStreams = this.store.pipe(
    select(RoomLocalStreamsSelectors.roomLocalStreams)
  );
  private readonly roomRemoteStreams = this.store.pipe(
    select(RoomRemoteStreamsSelectors.roomRemoteStreams)
  );

  constructor(
    private readonly store: Store,
    private readonly conferenceStreamsListService: ConferenceStreamsListService,
    private readonly localConferenceStreamsListService: LocalConferenceStreamsListService
  ) {}

  // TODO https://liveedev.atlassian.net/browse/YOUM-229
  async telemetryGet() {
    const roomLocalStreams = await firstValueFrom(this.roomLocalStreams, {
      defaultValue: []
    });
    const roomRemoteStreams = await firstValueFrom(this.roomRemoteStreams, {
      defaultValue: []
    });
    const cStreams = await firstValueFrom(
      this.conferenceStreamsListService.cStreams$,
      { defaultValue: [] }
    );

    const [localConferenceStream] =
      this.localConferenceStreamsListService.streams;

    return {
      userAgent: navigator.userAgent,
      rtcRtpSenderCapabilities: [
        RTCRtpSender.getCapabilities("video"),
        RTCRtpSender.getCapabilities("audio")
      ],
      rtcRtpReceiverCapabilities: [
        RTCRtpReceiver.getCapabilities("video"),
        RTCRtpReceiver.getCapabilities("audio")
      ],
      deviceRecvRtpCapabilities: localConferenceStream
        ? await firstValueFrom(localConferenceStream.recvRtpCapabilities$, {
            defaultValue: null
          })
        : null,
      deviceSendRtpCapabilities: localConferenceStream
        ? await firstValueFrom(localConferenceStream.sendRtpCapabilities$, {
            defaultValue: null
          })
        : null,
      roomLocalStreams,
      roomRemoteStreams,
      cStreams: await Promise.all(
        cStreams.map((_) => this.cStreamTelemetryGet(_))
      ),
      // YOUM-268: bounded screen-share sender state and recent history.
      screenShareDiagnostics: this.screenShareDiagnosticsGet()
    };
  }

  private screenShareDiagnosticsGet() {
    try {
      return screenShareDiagnosticsSnapshots(
        this.localConferenceStreamsListService.streams
      );
    } catch {
      return [];
    }
  }

  private async cStreamTelemetryGet(stream: AnyConferenceStream) {
    const streamData = stream.streamData;
    const streamApiCredentials = stream.streamApiCredentials;

    const stats = await firstValueFrom(stream.stats$, { defaultValue: [] });

    return {
      streamData,
      streamApiCredentials,
      stats
    };
  }
}
